import React, { useEffect, useState } from 'react';
import { Add } from '@mui/icons-material';
import {
    REmptyText,
    RErrorText,
    RModal,
    RPillButton,
    RRow,
    RStack,
    RToast,
} from 'recruit/components';
import { AddPaymentMethod } from './AddPaymentMethod';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { PaymentMethodRow } from './PaymentMethodRow';
import {
    deletePaymentMethod,
    getPaymentMethods,
    setDefaultPaymentMethod,
    setRecruitDefaultPaymentMethod,
} from './api';
import { PaymentMethod } from './types';

type AllowedKind = 'card' | 'bank';

interface ManagePaymentMethodsProps {
    allowedKinds?: AllowedKind[];
    showRecruitDefault?: boolean;
    showSubsDefault?: boolean;
}

export function ManagePaymentMethods({
    allowedKinds = ['card', 'bank'],
    showRecruitDefault = true,
    showSubsDefault = true,
}: ManagePaymentMethodsProps) {
    const [methods, setMethods] = useState<PaymentMethod[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [showAdd, setShowAdd] = useState(false);
    const [toast, setToast] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState<PaymentMethod | null>(null);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);

    function refetch() {
        getPaymentMethods()
            .then(setMethods)
            .catch(() => setLoadError('Could not load payment methods.'));
    }

    useEffect(refetch, []);

    useEffect(() => {
        if (!toast) return undefined;
        const t = window.setTimeout(() => setToast(null), 4000);
        return () => window.clearTimeout(t);
    }, [toast]);

    async function handleSetSubsDefault(method: PaymentMethod) {
        try {
            await setDefaultPaymentMethod(method.id);
            refetch();
            setToast('Default payment method updated.');
        } catch {
            setToast('Could not update default. Please try again.');
        }
    }

    async function handleSetRecruitDefault(method: PaymentMethod) {
        try {
            await setRecruitDefaultPaymentMethod(method.id);
            refetch();
            setToast('Recruit default updated.');
        } catch {
            setToast('Could not update recruit default. Please try again.');
        }
    }

    function startDelete(method: PaymentMethod) {
        if (method.is_default || method.is_recruit_default) {
            setToast('Set another payment method as default before deleting this one.');
            return;
        }
        setPendingDelete(method);
    }

    async function confirmDelete() {
        if (!pendingDelete) return;
        setDeleteSubmitting(true);
        try {
            await deletePaymentMethod(pendingDelete.id);
            setPendingDelete(null);
            refetch();
            setToast('Payment method removed.');
        } catch (err: unknown) {
            const detail =
                err && typeof err === 'object' && 'detail' in err && typeof (err as { detail: unknown }).detail === 'string'
                    ? (err as { detail: string }).detail
                    : 'Could not delete payment method.';
            setToast(detail);
        } finally {
            setDeleteSubmitting(false);
        }
    }

    if (loadError) {
        return <RErrorText>{loadError}</RErrorText>;
    }

    if (methods === null) {
        return <REmptyText>Loading…</REmptyText>;
    }

    return (
        <RStack gap="md">
            {methods.map((method) => (
                <PaymentMethodRow
                    key={method.id}
                    method={method}
                    showSubsDefault={showSubsDefault}
                    showRecruitDefault={showRecruitDefault}
                    onSetSubsDefault={() => handleSetSubsDefault(method)}
                    onSetRecruitDefault={() => handleSetRecruitDefault(method)}
                    onDelete={() => startDelete(method)}
                />
            ))}

            <RRow>
                <RPillButton
                    variant="secondary"
                    onClick={() => setShowAdd(true)}
                    icon={<Add sx={{ fontSize: 16 }} />}
                >
                    Add Payment Method
                </RPillButton>
            </RRow>

            {showAdd && (
                <RModal title="Add Payment Method" onClose={() => setShowAdd(false)} width={520}>
                    <AddPaymentMethod
                        allowedKinds={allowedKinds}
                        onSuccess={() => {
                            setShowAdd(false);
                            refetch();
                            setToast('Payment method added.');
                        }}
                        onCancel={() => setShowAdd(false)}
                    />
                </RModal>
            )}

            {pendingDelete && (
                <DeleteConfirmModal
                    method={pendingDelete}
                    submitting={deleteSubmitting}
                    onCancel={() => setPendingDelete(null)}
                    onConfirm={confirmDelete}
                />
            )}

            <RToast message={toast} />
        </RStack>
    );
}
